<script setup lang="ts">
/**
 * Uploadveld met drag-and-drop en een lijst van gekozen bestanden. Vervangt
 * `<v-input type="dropzone">`.
 *
 * Het model is een lijst `UploadedFile`: nieuwe uploads (`file`) en reeds opgeslagen
 * bijlagen (`database_saved`). De logica is 1-op-1 overgenomen uit VInput, inclusief
 * de fixes uit #6236 (limieten, foutmeldingen per bestand) en #6653 (drag-and-drop).
 *
 * `variant="image"`: één afbeelding als kaart met voorbeeld, een knop "Kies" en, enkel
 * als er een afbeelding is, "Verwijderen". Slepen kan op de hele kaart. Gebruikt voor
 * logo en achtergrond op Instellingen → Weergave (GH #7030), waar een bestandsnaam in
 * een lijst niets zegt en je wil zien wat er op je document komt.
 */
import { usePage } from '@inertiajs/vue3'
import { IconCloudUpload, IconPhoto, IconTrash } from '@tabler/icons-vue'
import { trans } from 'laravel-vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, useId } from 'vue'
import { formatUploadSize, isFileDrag } from '@/HelperFunctions'
import type { PageProps } from '@/Models/Inertia'
import VButton from '../VButton.vue'
import VSpinner from '../VSpinner.vue'
import VFormField from './VFormField.vue'
import type { FormFieldBaseProps, UploadedFile } from './types'
import { controlClasses, useFieldErrors, useFormFieldBindings, useSplitAttrs } from './useFormField'

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<UploadedFile[] | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps & {
            placeholder?: string | null
            multiple?: boolean
            /** Expliciete accept-lijst (extensies of MIME-types); wint van uploadContext. */
            acceptedFileTypes?: string[] | null
            /** Sleutel in config/uploads.php: types en maximale grootte. */
            uploadContext?: string | null
            /** `list` (standaard): sleepzone + bestandenlijst; `image`: één afbeelding als kaart. */
            variant?: 'list' | 'image'
            /**
             * Enkel `list`: `lg` maakt de sleepzone groot (zoals de uploadkaarten in de
             * PDF-instellingen), voor schermen waar uploaden de hoofdactie is.
             */
            size?: 'md' | 'lg'
            /** Enkel `image`: label van de kiesknop (vertaald). */
            chooseLabel?: string | null
            /** Enkel `image`: toont een spinner over het voorbeeld (upload bezig). */
            loading?: boolean
        }
    >(),
    {
        placeholder: null,
        variant: 'list',
        size: 'md',
        chooseLabel: null,
        loading: false,
        multiple: false,
        acceptedFileTypes: null,
        uploadContext: null,
        showErrors: true,
    }
)

/**
 * Fallback-lijst voor uploadvelden die noch een expliciete `acceptedFileTypes`
 * meegeven, noch een `uploadContext`. Bewust breed: dit was vóór #6236 de default.
 */
const DEFAULT_ACCEPTED_FILE_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
]

const page = usePage<PageProps>()
const { rootAttrs, controlAttrs } = useSplitAttrs()

// --- Upload limieten & foutweergave (#6236) ---

/**
 * Client-side geweigerde bestanden. Staat los van de serverfouten zodat een bestand
 * dat de browser al kan afkeuren nooit eerst volledig geüpload wordt.
 */
const localFileErrors = ref<string[]>([])

/** Limieten uit `config/uploads.php` via de globale Inertia-share. */
const uploadLimits = computed<{ extensions: string[]; maxSizeKb: number | null }>(() => {
    const context = props.uploadContext

    if (!context) {
        return { extensions: [], maxSizeKb: null }
    }

    const shared = page.props.upload_limits

    return {
        extensions: shared?.allowed_types?.[context] ?? [],
        maxSizeKb: shared?.max_size?.[context] ?? null,
    }
})

/**
 * De `accept`-lijst. Een expliciete lijst wint, daarna `uploadContext` (dezelfde
 * config als de server-validatieregel, dus ze kunnen niet uit elkaar lopen), anders
 * de brede fallback.
 */
const effectiveAcceptedFileTypes = computed<string[]>(() => {
    if (props.acceptedFileTypes !== null) {
        return props.acceptedFileTypes
    }

    const extensions = uploadLimits.value.extensions

    if (extensions.length > 0) {
        return extensions.map((extension) => `.${extension}`)
    }

    return DEFAULT_ACCEPTED_FILE_TYPES
})

/**
 * Leesbare opsomming ("PDF, JPG, PNG"). Een gebruiker heeft niets aan
 * "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet".
 */
const acceptedTypesLabel = computed<string>(() => {
    const labels = effectiveAcceptedFileTypes.value.map((type) =>
        type.startsWith('.') ? type.slice(1) : type.split('/').pop()!
    )

    return [...new Set(labels.map((label) => label.toUpperCase()))].join(', ')
})

function formatFileSize(sizeInKb: number): string {
    return formatUploadSize(sizeInKb, page.props.locale)
}

/**
 * Welke types mogen en hoe groot mag het bestand zijn. Vóór #6236 stond dit nergens,
 * waardoor een gebruiker pas na een mislukte upload kon raden waarom.
 */
const uploadHint = computed<string | null>(() => {
    const maxSizeKb = uploadLimits.value.maxSizeKb

    // Zonder uploadContext kennen we de limiet niet en tonen we geen halve hint.
    if (maxSizeKb === null) {
        return null
    }

    const parts: string[] = []

    if (acceptedTypesLabel.value !== '') {
        parts.push(acceptedTypesLabel.value)
    }

    parts.push(trans('validation.client.max_size', { size: formatFileSize(maxSizeKb) }))

    return parts.join(' — ')
})

/**
 * Hint en `example` samen op één grijze regel; zonder `example` staat de hint apart
 * vóór de fouten. Zo komen er nooit twee grijze regels onder elkaar (#6236).
 */
const standaloneHint = computed<string | null>(() => (props.example ? null : uploadHint.value))

const exampleLine = computed<string | null>(() => {
    if (!props.example) {
        return null
    }

    return uploadHint.value ? `${props.example} — ${uploadHint.value}` : props.example
})

/**
 * Array-regels zoals `attachments.*` leveren de errorkeys `attachments.0`, … terwijl
 * het veld `attachments` heet. We matchen daarom ook op `name.`-geprefixte keys en
 * vervangen de index door de bestandsnaam (#6236).
 */
const baseErrors = useFieldErrors(props)

const fieldErrors = computed<string[]>(() => {
    if (props.showErrors === false) {
        return []
    }

    // De fout op het veld zelf: een expliciete `error`-prop of anders `errors[name]`.
    // De fouten per bestand hieronder komen er altijd bij, ook met een expliciete prop.
    const messages: string[] = [...baseErrors.value]

    if (props.name) {
        const allErrors = (page.props.errors ?? {}) as Record<string, string>
        const prefix = `${props.name}.`

        // De index slaat op de lijst die de SERVER valideerde, en die is her-geïndexeerd
        // door FiltersDatabaseSavedAttachments: enkel nieuwe uploads blijven over. Zoeken
        // in de volledige lijst zou naar een reeds opgeslagen bijlage wijzen (#6236).
        const files: UploadedFile[] = (
            Array.isArray(modelValue.value) ? modelValue.value : []
        ).filter((file) => !file.database_saved)

        Object.keys(allErrors)
            .filter((key) => key.startsWith(prefix))
            .sort()
            .forEach((key) => {
                const index = Number(key.slice(prefix.length).split('.')[0])
                const fileName = Number.isNaN(index)
                    ? null
                    : (files[index]?.name ?? files[index]?.file?.name ?? null)

                messages.push(fileName ? `${fileName}: ${allErrors[key]}` : allErrors[key])
            })
    }

    return [...messages, ...localFileErrors.value]
})

const baseBindings = useFormFieldBindings(props, fieldErrors)
const fieldBindings = computed(() => ({
    ...baseBindings.value,
    example: exampleLine.value,
    hint: standaloneHint.value,
}))

const classes = computed<string>(() =>
    controlClasses({
        inputClasses: props.inputClasses,
        invalid: props.invalid,
        hasPrefix: false,
        hasSuffix: false,
    })
)

// --- Drag-and-drop (#6653) ---

/**
 * `dragenter`/`dragleave` vuren ook bij elk kind-element; zonder teller flikkert de
 * highlight telkens de muis van het ene kind naar het andere gaat.
 */
const isDragging = ref(false)
let dragDepth = 0

/**
 * Vangnet: eindigt een drag buiten de dropzone (Escape, drop elders), dan vuurt er
 * geen `dragleave` en zou de highlight blijven hangen.
 */
function resetDragState() {
    dragDepth = 0
    isDragging.value = false
}

onMounted(() => {
    window.addEventListener('dragend', resetDragState)
    window.addEventListener('drop', resetDragState)
})

onBeforeUnmount(() => {
    window.removeEventListener('dragend', resetDragState)
    window.removeEventListener('drop', resetDragState)
})

function handleDragEnter(event: DragEvent) {
    event.preventDefault()

    if (props.disabled || !isFileDrag(event)) {
        return
    }

    dragDepth++
    isDragging.value = true
}

function handleDragOver(event: DragEvent) {
    // Enkel sleepacties mét bestanden: tekst of een link valt door naar de globale
    // vangrail in app.ts, die er de "niet toegestaan"-cursor van maakt.
    if (!isFileDrag(event)) {
        return
    }

    // Zonder preventDefault op dragover staat de browser geen drop toe.
    event.preventDefault()

    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = props.disabled ? 'none' : 'copy'
    }
}

function handleDragLeave() {
    dragDepth = Math.max(0, dragDepth - 1)

    if (dragDepth === 0) {
        isDragging.value = false
    }
}

/**
 * Gesleepte bestanden gaan door dezelfde verwerking als gekozen bestanden.
 *
 * Bewust niet via flowbite's `update:modelValue`: die emit bij klikken-en-kiezen twee
 * keer (input + wrapper) en zou elk bestand dubbel toevoegen, en zijn drop-handler
 * geeft de ruwe, ongefilterde lijst. Een drop omzeilt ook het `accept`-attribuut, dus
 * `processFiles` is de enige client-side controle.
 *
 * Zie: #6653, https://numnum.freshdesk.com/a/tickets/13439
 */
function handleDrop(event: DragEvent) {
    event.preventDefault()
    resetDragState()

    // Tijdens een lopende upload (image-variant) geen nieuw bestand aanvaarden.
    if (!event.dataTransfer || !isFileDrag(event) || props.loading) {
        return
    }

    processFiles(Array.from(event.dataTransfer.files))
}

/** Dezelfde listeners op de dropzone én op de lijst eronder (die ook drops aanvaardt). */
const dragListeners = {
    dragenter: handleDragEnter,
    dragover: handleDragOver,
    dragleave: handleDragLeave,
    drop: handleDrop,
}

const inputRef = ref<HTMLInputElement | null>(null)

// De sleepzone is een <label for>; zonder id/name toch een unieke koppeling.
const generatedId = `dropzone-${useId()}`
const inputId = computed<string>(() => props.id ?? props.name ?? generatedId)

/** Opent de bestandskiezer; ook via het toetsenbord (Enter/Spatie op de sleepzone). */
function openFilePicker(): void {
    if (!props.disabled && !props.loading) {
        inputRef.value?.click()
    }
}

// addFiles: bestanden die buiten de dropzone binnenkomen (bv. een drop op de hele
// kluispagina) krijgen zo dezelfde type- en groottecontrole als een gewone keuze.
defineExpose({
    inputRef,
    focus: () => inputRef.value?.closest('label')?.focus(),
    addFiles: (files: File[]) => processFiles(files),
})

/** Native `change` van het verborgen file-input: klikken en kiezen. */
function handleFileUpload(event: Event) {
    const target = event.target as HTMLInputElement
    if (!target.files) {
        return
    }

    processFiles(Array.from(target.files))

    // Leegmaken zodat hetzelfde bestand opnieuw gekozen kan worden.
    target.value = ''
}

/**
 * Gedeelde verwerking voor kiezen én slepen: filteren op type en grootte, weigeringen
 * tonen, en toevoegen aan het model.
 */
function processFiles(selectedFiles: File[]) {
    // Weigeringen horen bij een vorige keuzeronde; altijd wissen.
    localFileErrors.value = []

    // Flowbite kent geen disabled-prop: het verborgen input is nooit echt uitgeschakeld.
    if (props.disabled || selectedFiles.length === 0) {
        return
    }

    let files = selectedFiles
    let currentFiles: UploadedFile[] = Array.isArray(modelValue.value) ? modelValue.value : []

    if (!props.multiple || props.variant === 'image') {
        files = files.slice(-1)
        currentFiles = []
    }

    const rejections: string[] = []
    const maxSizeKb = uploadLimits.value.maxSizeKb
    const acceptedTypes = effectiveAcceptedFileTypes.value

    // Hetzelfde bestand (naam, grootte, laatst gewijzigd) maar één keer in de lijst.
    // Aanleiding: bij uitgaven wordt elk bestand een aparte uitgave, en een twee keer
    // gesleept bonnetje kwam zo dubbel bij de boekhouder terecht. GH #7290
    const fileKey = (file: File) => `${file.name}|${file.size}|${file.lastModified}`
    const seen = new Set(
        currentFiles
            .map((current) => current.file)
            .filter((file): file is File => file instanceof File)
            .map(fileKey)
    )

    const newFiles: UploadedFile[] = files
        .filter((file) => {
            if (seen.has(fileKey(file))) {
                rejections.push(`${file.name}: ${trans('validation.client.file_already_added')}`)

                return false
            }

            seen.add(fileKey(file))
            const typeAllowed = acceptedTypes.some(
                (type) =>
                    file.type.startsWith(type) ||
                    file.name.toLowerCase().endsWith(type.toLowerCase())
            )

            if (!typeAllowed) {
                rejections.push(
                    `${file.name}: ${trans('validation.client.file_type_not_allowed', {
                        values: acceptedTypesLabel.value,
                    })}`
                )

                return false
            }

            // file.size is bytes, de config-limiet kilobytes (net als Laravel's max:).
            if (maxSizeKb !== null && file.size / 1024 > maxSizeKb) {
                rejections.push(
                    `${file.name}: ${trans('validation.client.file_too_large', {
                        max: formatFileSize(maxSizeKb),
                    })}`
                )

                return false
            }

            return true
        })
        .map((file) => ({
            file,
            tempUrl: URL.createObjectURL(file),
            name: file.name,
        }))

    localFileErrors.value = rejections
    modelValue.value = [...currentFiles, ...newFiles]
}

/**
 * Verwijdert een bestand uit de lijst en wist alle client-side weigeringen: die horen
 * bij de vorige keuzeronde en zijn niet 1-op-1 aan een bestand in de lijst te koppelen.
 *
 * Maakt een nieuwe lijst i.p.v. te splicen: VInput muteerde de prop-array, waardoor
 * een parent met een eigen watcher (zonder `deep`) de wijziging niet zag.
 */
function removeFile(index: number) {
    if (!Array.isArray(modelValue.value)) {
        return
    }

    modelValue.value = modelValue.value.filter((_, fileIndex) => fileIndex !== index)
    localFileErrors.value = []
}
</script>

<template>
    <VFormField v-bind="{ ...fieldBindings, ...rootAttrs }">
        <template v-if="$slots.hint" #hint><slot name="hint" /></template>
        <div
            v-if="variant === 'image'"
            :class="[
                // v-field__raw: de algemene .v-field-regels voor span/div/label (padding,
                // flex-basis, vet) vervormden anders de spinner en het voorbeeld.
                'v-field__raw v-dropzone v-dropzone--image',
                {
                    'v-dropzone--active': isDragging,
                    'v-dropzone--disabled': disabled,
                },
            ]"
            v-on="dragListeners"
        >
            <label
                :for="inputId"
                class="v-dropzone__preview"
                :aria-label="chooseLabel ?? label ?? undefined"
                :tabindex="disabled || loading ? -1 : 0"
                @keydown.enter.prevent="openFilePicker"
                @keydown.space.prevent="openFilePicker"
            >
                <img
                    v-if="modelValue?.length && !isDragging"
                    :src="modelValue[0].tempUrl"
                    :alt="modelValue[0].name ?? ''"
                    class="v-dropzone__image"
                />
                <span v-else class="v-dropzone__empty">
                    <IconPhoto
                        v-if="!isDragging"
                        class="v-dropzone__empty-icon"
                        aria-hidden="true"
                    />
                    <IconCloudUpload v-else class="v-dropzone__empty-icon" aria-hidden="true" />
                    <span class="v-dropzone__text">
                        {{
                            isDragging
                                ? $t('global.upload_drop_here')
                                : (placeholder ?? $t('global.upload_click_or_drop'))
                        }}
                    </span>
                </span>
                <span v-if="loading" class="v-dropzone__loading">
                    <VSpinner size="lg" />
                </span>
                <input
                    v-bind="controlAttrs"
                    ref="inputRef"
                    :id="inputId"
                    :name="name ?? undefined"
                    type="file"
                    class="hidden"
                    :disabled="disabled || loading"
                    :accept="effectiveAcceptedFileTypes.join(',')"
                    @change="handleFileUpload"
                />
            </label>
            <div class="v-dropzone__actions">
                <VButton
                    variant="secondary"
                    size="sm"
                    :icon="IconCloudUpload"
                    :disabled="disabled || loading"
                    @click="openFilePicker"
                >
                    {{ chooseLabel ?? $t('global.upload_click_or_drop') }}
                </VButton>
                <VButton
                    v-if="modelValue?.length"
                    variant="outline-danger"
                    size="sm"
                    :icon="IconTrash"
                    :disabled="disabled || loading"
                    @click="removeFile(0)"
                >
                    {{ $t('global.delete') }}
                </VButton>
            </div>
        </div>

        <!-- Eén blok: sleepzone + bestandenlijst, met dezelfde rand. Vroeger was dit
             flowbite's file-input met globale CSS eroverheen (.dropzone in _v-input.scss),
             die o.a. de tekst "of sleep hierheen" verborg: niemand zag dat slepen kon.
             Beide delen aanvaarden drops (#6653). -->
        <div
            v-else
            :class="[
                classes,
                'v-dropzone',
                {
                    'v-dropzone--lg': size === 'lg',
                    'v-dropzone--active': isDragging,
                    'v-dropzone--disabled': disabled,
                },
            ]"
            v-on="dragListeners"
        >
            <label
                :for="inputId"
                class="v-dropzone__target"
                :tabindex="disabled ? -1 : 0"
                @keydown.enter.prevent="openFilePicker"
                @keydown.space.prevent="openFilePicker"
            >
                <IconCloudUpload class="v-dropzone__icon" aria-hidden="true" />
                <p class="v-dropzone__text">
                    {{
                        isDragging
                            ? $t('global.upload_drop_here')
                            : (placeholder ?? $t('global.upload_click_or_drop'))
                    }}
                </p>
                <input
                    v-bind="controlAttrs"
                    ref="inputRef"
                    :id="inputId"
                    :name="name ?? undefined"
                    type="file"
                    class="hidden"
                    :multiple="multiple"
                    :disabled="disabled"
                    :accept="effectiveAcceptedFileTypes.join(',')"
                    @change="handleFileUpload"
                />
            </label>

            <ul v-if="modelValue?.length" class="v-dropzone__files">
                <li v-for="(attachment, index) in modelValue" :key="'attachment' + index">
                    <a :href="attachment.tempUrl" target="_blank" class="v-dropzone__file-name">
                        {{ attachment.name ?? attachment.file?.name }}
                    </a>
                    <button
                        type="button"
                        class="v-dropzone__remove"
                        :aria-label="$t('global.delete')"
                        :disabled="disabled"
                        @click="removeFile(index)"
                    >
                        <IconTrash class="w-5 h-5" aria-hidden="true" />
                    </button>
                </li>
            </ul>
        </div>
    </VFormField>
</template>

<style scoped lang="scss">
// Selectors met element + klasse: `.v-field.v-field label`/`div` (_v-field.scss) zijn
// specifieker dan één klasse en zouden anders de sleepzone als veldlabel stylen.
div.v-dropzone {
    @apply flex flex-col w-full bg-white border border-gray-300 rounded overflow-hidden;
}

label.v-dropzone__target {
    @apply flex items-center justify-center gap-2 m-0 px-3 py-2 min-h-[38px] w-full;
    @apply bg-gray-50 border-2 border-dashed border-transparent rounded;
    @apply text-sm font-normal leading-5 text-gray-500 text-center cursor-pointer;
    @apply transition-colors;

    &:hover,
    &:focus-visible {
        @apply bg-gray-100 text-gray-700 outline-none;
    }
    &:focus-visible {
        @apply border-primary;
    }
}

.v-dropzone__icon {
    @apply w-5 h-5 flex-none;
}

p.v-dropzone__text {
    @apply m-0 p-0;
}

// Tijdens het slepen: duidelijke drop-target. `!important`-vrij, want dit zijn onze
// eigen elementen.
div.v-dropzone--active {
    @apply border-primary;

    label.v-dropzone__target {
        @apply border-primary text-primary;
        background-color: var(--hover);
    }
}

div.v-dropzone--disabled label.v-dropzone__target {
    @apply cursor-not-allowed opacity-60;

    &:hover {
        @apply bg-gray-50 text-gray-500;
    }
}

// --- size="lg" (enkel list) ---
div.v-dropzone--lg {
    label.v-dropzone__target {
        @apply flex-col gap-3 h-48 text-base;
    }

    .v-dropzone__icon {
        @apply w-12 h-12 text-gray-300;
    }

    &.v-dropzone--active .v-dropzone__icon {
        @apply text-primary;
    }

    // Veel bestanden tegelijk: de modal blijft hanteerbaar.
    ul.v-dropzone__files {
        @apply max-h-60 overflow-y-auto;
    }
}

// --- variant="image" ---
div.v-dropzone--image {
    @apply flex flex-col w-full bg-white border border-gray-300 rounded overflow-hidden;
    @apply dark:bg-gray-800 dark:border-gray-600;
}

label.v-dropzone__preview {
    @apply relative flex items-center justify-center m-0 p-4 h-40 w-full;
    @apply bg-gray-50 border-2 border-dashed border-transparent cursor-pointer transition-colors;
    @apply dark:bg-gray-900;

    &:hover,
    &:focus-visible {
        @apply bg-gray-100 outline-none dark:bg-gray-700;
    }
    &:focus-visible {
        @apply border-primary;
    }
}

img.v-dropzone__image {
    @apply max-h-full max-w-full object-contain;
}

span.v-dropzone__empty {
    @apply flex flex-col items-center gap-2 text-sm text-gray-500 text-center dark:text-gray-400;

    // `body span` (global.scss) zet elke span op text-xs en de vaste tekstkleur.
    span.v-dropzone__text {
        @apply text-sm text-inherit;
    }
}

.v-dropzone__empty-icon {
    @apply w-10 h-10 text-gray-300;
}

span.v-dropzone__loading {
    @apply absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-gray-800/70;
}

div.v-dropzone__actions {
    @apply flex flex-wrap items-center justify-center gap-2 px-3 py-2 border-t border-gray-200;
    @apply dark:border-gray-700;
}

div.v-dropzone--image.v-dropzone--active {
    @apply border-primary;

    label.v-dropzone__preview {
        @apply border-primary text-primary;
        background-color: var(--hover);
    }
}

div.v-dropzone--image.v-dropzone--disabled label.v-dropzone__preview {
    @apply cursor-not-allowed opacity-60;
}

ul.v-dropzone__files {
    @apply m-0 p-0 list-none border-t border-gray-300;

    li {
        @apply flex items-center justify-between gap-2 px-3 py-1.5 text-sm;

        & + li {
            @apply border-t border-gray-200;
        }
        &:hover {
            background-color: var(--hover);
        }
    }
}

.v-dropzone__file-name {
    @apply truncate hover:underline;
}

.v-dropzone__remove {
    @apply flex-none text-gray-400 hover:text-danger disabled:cursor-not-allowed;
}
</style>
